import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { ChevronDown, ChevronUp, FileText, Users, Clock, Lock, AlertCircle, DollarSign, Scale, HelpCircle } from 'lucide-react';
export default function Terms() {
  const [openSections, setOpenSections] = useState({
    definitions: true,
    instruction: true,
    duties: true,
    services: true,
    fees: true,
    timelines: true,
    dataProtection: true,
    intellectual: true,
    cancellation: true,
    liability: true,
    complaints: true,
    governing: true,
    amendments: true
  });
  const toggleSection = section => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  return <main className="min-h-screen bg-white" data-id="element-751">
      <Helmet data-id="element-752">
        <title data-id="element-753">Terms & Conditions | Medicolegal Psychiatry</title>
        <meta name="description" content="Terms and conditions for Medicolegal Psychiatry services, outlining service agreements, obligations, data protection, and fees." data-id="element-754" />
        <meta name="keywords" content="Terms and Conditions, Service Agreement, Expert Witness Terms, Medicolegal Psychiatry" data-id="element-755" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-primary text-white py-10" data-id="element-756">
        <div className="container mx-auto px-4" data-id="element-757">
          <div className="flex items-center justify-center mb-4" data-id="element-758">
            <Scale className="h-12 w-12 mr-4" data-id="element-759" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold" data-id="element-760">
              Terms & Conditions
            </h1>
          </div>
        </div>
      </section>
      {/* Terms Content */}
      <section className="py-12" data-id="element-761">
        <div className="container mx-auto px-4" data-id="element-762">
          <div className="max-w-3xl mx-auto" data-id="element-763">
            <p className="text-slate-dark text-lg mb-8" data-id="element-764">
              These Terms and Conditions govern the provision of medico-legal
              psychiatric services by Dr. Imtiaz Syed (operating as "Medicolegal
              Psychiatry") to the instructing party. By instructing Dr. Syed,
              you are agreeing to these terms.
            </p>
            <div className="space-y-4 mb-8" data-id="element-765">
              {/* Definitions Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-766">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.definitions ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('definitions')} data-id="element-767">
                  <div className="flex items-center" data-id="element-768">
                    <FileText className="h-5 w-5 mr-2" data-id="element-769" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-770">
                      1. Definitions
                    </h2>
                  </div>
                  {openSections.definitions ? <ChevronUp className="h-5 w-5" data-id="element-771" /> : <ChevronDown className="h-5 w-5" data-id="element-772" />}
                </button>
                {openSections.definitions && <div className="p-6 bg-white" data-id="element-773">
                    <ul className="space-y-2 text-slate-dark" data-id="element-774">
                      <li data-id="element-775">
                        <strong data-id="element-776">"Dr. Syed"</strong> refers to Dr. Imtiaz Syed,
                        operating as Medicolegal Psychiatry.
                      </li>
                      <li data-id="element-777">
                        <strong data-id="element-778">"Instructing Party"</strong> refers to the
                        solicitor, law firm, court, tribunal, or other entity
                        that instructs Dr. Syed.
                      </li>
                      <li data-id="element-779">
                        <strong data-id="element-780">"Subject"</strong> refers to the individual who
                        is to be assessed by Dr. Syed.
                      </li>
                      <li data-id="element-781">
                        <strong data-id="element-782">"Services"</strong> refers to the medico-legal
                        psychiatric assessments, report writing, and expert
                        witness testimony provided by Dr. Syed.
                      </li>
                    </ul>
                  </div>}
              </div>
              {/* Instruction Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-783">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.instruction ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('instruction')} data-id="element-784">
                  <div className="flex items-center" data-id="element-785">
                    <FileText className="h-5 w-5 mr-2" data-id="element-786" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-787">
                      2. Instruction and Acceptance
                    </h2>
                  </div>
                  {openSections.instruction ? <ChevronUp className="h-5 w-5" data-id="element-788" /> : <ChevronDown className="h-5 w-5" data-id="element-789" />}
                </button>
                {openSections.instruction && <div className="p-6 bg-white" data-id="element-790">
                    <p className="text-slate-dark mb-4" data-id="element-791">
                      All instructions must be provided in writing, typically
                      via email to enquiries@medicolegal-psychiatry.co.uk. Dr.
                      Syed reserves the right to decline any instruction at his
                      discretion, particularly if there is a conflict of
                      interest or if the instruction falls outside his areas of
                      expertise.
                    </p>
                    <p className="text-slate-dark" data-id="element-792">
                      Upon acceptance of an instruction, Dr. Syed will provide a
                      Service Agreement outlining the specific services to be
                      provided, estimated timeframes, and fee structure. Work
                      will commence once this agreement has been signed and
                      returned by the Instructing Party.
                    </p>
                  </div>}
              </div>
              {/* Expert's Duties Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-793">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.duties ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('duties')} data-id="element-794">
                  <div className="flex items-center" data-id="element-795">
                    <Scale className="h-5 w-5 mr-2" data-id="element-796" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-797">
                      3. Expert's Duties
                    </h2>
                  </div>
                  {openSections.duties ? <ChevronUp className="h-5 w-5" data-id="element-798" /> : <ChevronDown className="h-5 w-5" data-id="element-799" />}
                </button>
                {openSections.duties && <div className="p-6 bg-white" data-id="element-800">
                    <p className="text-slate-dark" data-id="element-801">
                      Dr. Syed acknowledges and accepts the overriding duty to
                      the court as set out in the Civil Procedure Rules (CPR
                      Part 35), Family Procedure Rules (FPR Part 25), or
                      Criminal Procedure Rules (CrPR Part 19) as applicable. All
                      assessments and reports will be conducted with
                      impartiality, objectivity, and independence, regardless of
                      the instructing party.
                    </p>
                  </div>}
              </div>
              {/* Services Provided Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-802">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.services ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('services')} data-id="element-803">
                  <div className="flex items-center" data-id="element-804">
                    <Users className="h-5 w-5 mr-2" data-id="element-805" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-806">
                      4. Services Provided
                    </h2>
                  </div>
                  {openSections.services ? <ChevronUp className="h-5 w-5" data-id="element-807" /> : <ChevronDown className="h-5 w-5" data-id="element-808" />}
                </button>
                {openSections.services && <div className="p-6 bg-white" data-id="element-809">
                    <p className="text-slate-dark mb-4" data-id="element-810">
                      Services may include, but are not limited to:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-811">
                      <li data-id="element-812">
                        Review of documentation provided by the Instructing
                        Party
                      </li>
                      <li data-id="element-813">
                        Psychiatric assessment of the Subject (remote or
                        in-person)
                      </li>
                      <li data-id="element-814">Preparation of medico-legal reports</li>
                      <li data-id="element-815">Attendance at conferences with counsel</li>
                      <li data-id="element-816">
                        Expert witness testimony in court or tribunal
                        proceedings
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-817">
                      The specific services to be provided will be detailed in
                      the Service Agreement.
                    </p>
                  </div>}
              </div>
              {/* Fees and Payment Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-818">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.fees ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('fees')} data-id="element-819">
                  <div className="flex items-center" data-id="element-820">
                    <DollarSign className="h-5 w-5 mr-2" data-id="element-821" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-822">
                      5. Fees and Payment
                    </h2>
                  </div>
                  {openSections.fees ? <ChevronUp className="h-5 w-5" data-id="element-823" /> : <ChevronDown className="h-5 w-5" data-id="element-824" />}
                </button>
                {openSections.fees && <div className="p-6 bg-white" data-id="element-825">
                    <p className="text-slate-dark mb-4" data-id="element-826">
                      Fees are calculated based on the complexity of the case,
                      volume of documentation, type of assessment required, and
                      estimated time involved. The fee structure will be
                      outlined in the Service Agreement and may include:
                    </p>
                    <ul className="list-disc pl-6 mb-4 text-slate-dark" data-id="element-827">
                      <li data-id="element-828">
                        Hourly rates for document review, report writing, and
                        conferences
                      </li>
                      <li data-id="element-829">Fixed fees for assessments</li>
                      <li data-id="element-830">Daily rates for court attendance</li>
                      <li data-id="element-831">Travel expenses and time (where applicable)</li>
                      <li data-id="element-832">
                        Cancellation fees (as per our Cancellation Policy)
                      </li>
                    </ul>
                    <p className="text-slate-dark" data-id="element-833">
                      Payment terms are 30 days from the date of invoice. For
                      Legal Aid cases, Dr. Syed will work within the approved
                      funding limits, provided these are agreed upon in advance.
                    </p>
                  </div>}
              </div>
              {/* Timelines and Delivery */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-834">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.timelines ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('timelines')} data-id="element-835">
                  <div className="flex items-center" data-id="element-836">
                    <Clock className="h-5 w-5 mr-2" data-id="element-837" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-838">
                      6. Timelines and Delivery
                    </h2>
                  </div>
                  {openSections.timelines ? <ChevronUp className="h-5 w-5" data-id="element-839" /> : <ChevronDown className="h-5 w-5" data-id="element-840" />}
                </button>
                {openSections.timelines && <div className="p-6 bg-white" data-id="element-841">
                    <p className="text-slate-dark mb-4" data-id="element-842">
                      Dr. Syed aims to deliver reports within 10 working days of
                      the assessment, subject to the complexity of the case and
                      volume of documentation. Expedited reports (within 5
                      working days) may be available by prior arrangement and
                      may incur additional fees.
                    </p>
                    <p className="text-slate-dark" data-id="element-843">
                      While every effort will be made to meet agreed deadlines,
                      Dr. Syed cannot be held liable for delays resulting from
                      factors outside his control, such as the late provision of
                      documentation or the Subject's non-attendance.
                    </p>
                  </div>}
              </div>
              {/* Confidentiality and Data Protection */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-844">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.dataProtection ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('dataProtection')} data-id="element-845">
                  <div className="flex items-center" data-id="element-846">
                    <Lock className="h-5 w-5 mr-2" data-id="element-847" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-848">
                      7. Confidentiality and Data Protection
                    </h2>
                  </div>
                  {openSections.dataProtection ? <ChevronUp className="h-5 w-5" data-id="element-849" /> : <ChevronDown className="h-5 w-5" data-id="element-850" />}
                </button>
                {openSections.dataProtection && <div className="p-6 bg-white" data-id="element-851">
                    <p className="text-slate-dark mb-4" data-id="element-852">
                      Dr. Syed will handle all information in accordance with
                      the UK General Data Protection Regulation (UK GDPR), the
                      Data Protection Act 2018, and relevant professional
                      guidelines. Please refer to our Privacy Policy for
                      detailed information on how we collect, use, and protect
                      personal data.
                    </p>
                    <p className="text-slate-dark" data-id="element-853">
                      The Instructing Party is responsible for obtaining
                      appropriate consent from the Subject for the sharing of
                      their personal data with Dr. Syed for the purpose of
                      assessment and report preparation.
                    </p>
                  </div>}
              </div>
              {/* Intellectual Property */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-854">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.intellectual ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('intellectual')} data-id="element-855">
                  <div className="flex items-center" data-id="element-856">
                    <FileText className="h-5 w-5 mr-2" data-id="element-857" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-858">
                      8. Intellectual Property
                    </h2>
                  </div>
                  {openSections.intellectual ? <ChevronUp className="h-5 w-5" data-id="element-859" /> : <ChevronDown className="h-5 w-5" data-id="element-860" />}
                </button>
                {openSections.intellectual && <div className="p-6 bg-white" data-id="element-861">
                    <p className="text-slate-dark" data-id="element-862">
                      All reports and documents prepared by Dr. Syed remain his
                      intellectual property. The Instructing Party is granted a
                      license to use these documents for the specific legal
                      proceedings for which they were commissioned. Any other
                      use requires Dr. Syed's written permission.
                    </p>
                  </div>}
              </div>
              {/* Cancellation and Rescheduling */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-863">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.cancellation ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('cancellation')} data-id="element-864">
                  <div className="flex items-center" data-id="element-865">
                    <Clock className="h-5 w-5 mr-2" data-id="element-866" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-867">
                      9. Cancellation and Rescheduling
                    </h2>
                  </div>
                  {openSections.cancellation ? <ChevronUp className="h-5 w-5" data-id="element-868" /> : <ChevronDown className="h-5 w-5" data-id="element-869" />}
                </button>
                {openSections.cancellation && <div className="p-6 bg-white" data-id="element-870">
                    <p className="text-slate-dark" data-id="element-871">
                      Please refer to our separate Cancellation Policy for
                      detailed terms regarding the cancellation or rescheduling
                      of appointments and court attendance.
                    </p>
                  </div>}
              </div>
              {/* Liability */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-872">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.liability ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('liability')} data-id="element-873">
                  <div className="flex items-center" data-id="element-874">
                    <AlertCircle className="h-5 w-5 mr-2" data-id="element-875" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-876">
                      10. Liability
                    </h2>
                  </div>
                  {openSections.liability ? <ChevronUp className="h-5 w-5" data-id="element-877" /> : <ChevronDown className="h-5 w-5" data-id="element-878" />}
                </button>
                {openSections.liability && <div className="p-6 bg-white" data-id="element-879">
                    <p className="text-slate-dark" data-id="element-880">
                      Dr. Syed maintains appropriate professional indemnity
                      insurance. His liability is limited to the fees paid for
                      the specific services provided. Dr. Syed is not liable for
                      any consequential, indirect, or special losses arising
                      from the provision of his services.
                    </p>
                  </div>}
              </div>
              {/* Complaints */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-881">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.complaints ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('complaints')} data-id="element-882">
                  <div className="flex items-center" data-id="element-883">
                    <HelpCircle className="h-5 w-5 mr-2" data-id="element-884" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-885">
                      11. Complaints
                    </h2>
                  </div>
                  {openSections.complaints ? <ChevronUp className="h-5 w-5" data-id="element-886" /> : <ChevronDown className="h-5 w-5" data-id="element-887" />}
                </button>
                {openSections.complaints && <div className="p-6 bg-white" data-id="element-888">
                    <p className="text-slate-dark" data-id="element-889">
                      If the Instructing Party is dissatisfied with any aspect
                      of Dr. Syed's services, they should first raise their
                      concerns directly with Dr. Syed. If the matter cannot be
                      resolved, the Instructing Party may refer to the relevant
                      professional body (e.g., General Medical Council, Royal
                      College of Psychiatrists) as appropriate.
                    </p>
                  </div>}
              </div>
              {/* Governing Law */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-890">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.governing ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('governing')} data-id="element-891">
                  <div className="flex items-center" data-id="element-892">
                    <Scale className="h-5 w-5 mr-2" data-id="element-893" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-894">
                      12. Governing Law
                    </h2>
                  </div>
                  {openSections.governing ? <ChevronUp className="h-5 w-5" data-id="element-895" /> : <ChevronDown className="h-5 w-5" data-id="element-896" />}
                </button>
                {openSections.governing && <div className="p-6 bg-white" data-id="element-897">
                    <p className="text-slate-dark" data-id="element-898">
                      These Terms and Conditions are governed by the laws of
                      England and Wales. Any disputes arising from these terms
                      shall be subject to the exclusive jurisdiction of the
                      courts of England and Wales.
                    </p>
                  </div>}
              </div>
              {/* Amendments */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-899">
                <button className={`w-full px-6 py-4 text-left flex justify-between items-center ${openSections.amendments ? 'bg-primary text-white' : 'bg-gray-50 text-primary'}`} onClick={() => toggleSection('amendments')} data-id="element-900">
                  <div className="flex items-center" data-id="element-901">
                    <FileText className="h-5 w-5 mr-2" data-id="element-902" />
                    <h2 className="text-xl font-serif font-bold" data-id="element-903">
                      13. Amendments
                    </h2>
                  </div>
                  {openSections.amendments ? <ChevronUp className="h-5 w-5" data-id="element-904" /> : <ChevronDown className="h-5 w-5" data-id="element-905" />}
                </button>
                {openSections.amendments && <div className="p-6 bg-white" data-id="element-906">
                    <p className="text-slate-dark" data-id="element-907">
                      Dr. Syed reserves the right to amend these Terms and
                      Conditions from time to time. The latest version will
                      always be available on our website. The version in effect
                      at the time of instruction will apply to that specific
                      case.
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}